import type { ReactNode, Ref } from 'react';
import type { TextFieldProps as AriaTextFieldProps } from 'react-aria-components';
import {
  TextArea as AriaTextArea,
  TextField as AriaTextField,
} from 'react-aria-components';

import { HintText } from '@/lib/base/forms/hint-text';
import { Label } from '@/lib/base/forms/input/label';
import type { SelectSize } from '@/lib/base/forms/select/select-shared';
import { sizes } from '@/lib/base/forms/select/select-shared';
import { cx } from '@/utils/cx';

/** The shared field-control scale — same five steps as Input and Select. */
type TextAreaSize = SelectSize;
type TextAreaResize = 'none' | 'vertical' | 'both';

export interface TextAreaProps extends Omit<
  AriaTextFieldProps,
  'children' | 'className'
> {
  label?: string | ReactNode;
  hint?: ReactNode;
  placeholder?: string;
  /** Initial height hint (user remains free to resize). */
  rows?: number;
  /** Resize behavior. Defaults to 'vertical'. */
  resize?: TextAreaResize;
  /** Size preset matching Input. Defaults to 'sm'. */
  size?: TextAreaSize;
  /** Suppresses error UI even when isInvalid is true. Defaults to true (show error). */
  showError?: boolean;
  /** Hide the required indicator next to the label. */
  hideRequiredIndicator?: boolean;
  className?: string;
  wrapperClassName?: string;
  textareaClassName?: string;
  ref?: Ref<HTMLTextAreaElement>;
}

const resizeClasses: Record<TextAreaResize, string> = {
  none: 'resize-none',
  vertical: 'resize-y',
  both: 'resize',
};

export const TextArea = ({
  label,
  hint,
  placeholder,
  rows = 3,
  size = 'sm',
  resize = 'vertical',
  showError,
  hideRequiredIndicator,
  className,
  wrapperClassName,
  textareaClassName,
  ref,
  ...textFieldProps
}: TextAreaProps) => {
  const effectiveIsInvalid = (showError ?? true) && !!textFieldProps.isInvalid;

  return (
    <AriaTextField
      aria-label={!label ? placeholder : undefined}
      {...textFieldProps}
      isInvalid={effectiveIsInvalid}
      data-input-wrapper
      className={cx(
        'group flex h-max w-full flex-col items-start justify-start gap-1.5',
        className
      )}
    >
      {({ isRequired, isInvalid }) => (
        <>
          {label && (
            <Label
              isRequired={
                hideRequiredIndicator ? !hideRequiredIndicator : isRequired
              }
            >
              {label}
            </Label>
          )}

          <div
            className={cx(
              'relative flex w-full rounded-lg bg-primary shadow-xs ring-1 ring-primary transition-shadow duration-100 ease-linear ring-inset',
              'focus-within:ring-2 focus-within:ring-brand',
              'group-disabled:cursor-not-allowed group-disabled:bg-disabled_subtle group-disabled:ring-disabled',
              'group-invalid:ring-error_subtle',
              'focus-within:group-invalid:ring-2 focus-within:group-invalid:ring-error',
              wrapperClassName
            )}
          >
            <AriaTextArea
              ref={ref}
              placeholder={placeholder}
              rows={rows}
              className={cx(
                'm-0 w-full bg-transparent text-primary ring-0 outline-hidden placeholder:text-placeholder',
                'group-disabled:cursor-not-allowed group-disabled:text-disabled',
                // Padding AND font-size both come from the shared scale — the
                // size prop is the single source of truth for a control's
                // geometry. Never reintroduce a `text-*` in the base classes:
                // that is what forced the old padding-only local table, and it
                // let ancestor `[&_textarea]:text-*` overrides silently win.
                sizes[size].root,
                resizeClasses[resize],
                textareaClassName
              )}
            />
          </div>

          {hint && (
            <HintText isInvalid={isInvalid} showError={showError}>
              {hint}
            </HintText>
          )}
        </>
      )}
    </AriaTextField>
  );
};

TextArea.displayName = 'TextArea';
